"use client";

import * as React from "react";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type SizeRow = {
  provider: string;
  commission: string;
  features: string;
  support: string;
  fit: string;
  matched: boolean;
};
const brands = [{
  id: "siteminder",
  label: "SiteMinder"
}, {
  id: "cloudbeds",
  label: "Cloudbeds"
}, {
  id: "d-edge",
  label: "D-EDGE"
}, {
  id: "hotelrevbaba",
  label: "HotelRevBaBa"
}];
const siteminderRows: SizeRow[] = [{
  provider: "Basic",
  commission: "15%",
  features: "Standard Booking",
  support: "Email",
  fit: "Good for small hotels",
  matched: false
}, {
  provider: "Pro",
  commission: "12%",
  features: "Advanced Analytics",
  support: "24/7 Phone",
  fit: "Best for mid-size",
  matched: false
}, {
  provider: "Enterprise",
  commission: "10%",
  features: "Full API Access",
  support: "Dedicated",
  fit: "Industry standard",
  matched: true
}, {
  provider: "Custom",
  commission: "8%",
  features: "White Label",
  support: "Priority",
  fit: "High volume focus",
  matched: false
}, {
  provider: "Global",
  commission: "7%",
  features: "Multi-Property",
  support: "Global",
  fit: "Enterprise scale",
  matched: false
}];
const cloudbedsRows: SizeRow[] = [{
  provider: "Basic",
  commission: "14%",
  features: "PMS Integration",
  support: "Email",
  fit: "Easy setup",
  matched: false
}, {
  provider: "Pro",
  commission: "11%",
  features: "Yield Management",
  support: "24/7 Phone",
  fit: "Strong automation",
  matched: false
}, {
  provider: "Enterprise",
  commission: "9%",
  features: "Direct Booking",
  support: "Dedicated",
  fit: "Most popular choice",
  matched: true
}, {
  provider: "Custom",
  commission: "7%",
  features: "Custom API",
  support: "Priority",
  fit: "Scalable solution",
  matched: false
}, {
  provider: "Global",
  commission: "6%",
  features: "Chain Management",
  support: "Global",
  fit: "Robust platform",
  matched: false
}];
const dedgeRows: SizeRow[] = [{
  provider: "Basic",
  commission: "16%",
  features: "OTA Sync",
  support: "Email",
  fit: "Reliable sync",
  matched: false
}, {
  provider: "Pro",
  commission: "13%",
  features: "Revenue Tools",
  support: "24/7 Phone",
  fit: "Good analytics",
  matched: false
}, {
  provider: "Enterprise",
  commission: "11%",
  features: "Direct Sales",
  support: "Dedicated",
  fit: "Top tier choice",
  matched: true
}, {
  provider: "Custom",
  commission: "9%",
  features: "Custom Branding",
  support: "Priority",
  fit: "Flexible design",
  matched: false
}, {
  provider: "Global",
  commission: "8%",
  features: "Global Distribution",
  support: "Global",
  fit: "Market leader",
  matched: false
}];
const hotelrevbabaRows: SizeRow[] = [{
  provider: "Audit",
  commission: "0%",
  features: "Revenue Analysis",
  support: "Expert Call",
  fit: "Free initial audit",
  matched: false
}, {
  provider: "Basic",
  commission: "5%",
  features: "OTA Optimization",
  support: "Email/Chat",
  fit: "High conversion",
  matched: false
}, {
  provider: "Premium",
  commission: "3%",
  features: "Full Revenue Mgmt",
  support: "Dedicated Expert",
  fit: "Best ROI strategy",
  matched: true
}, {
  provider: "Enterprise",
  commission: "2%",
  features: "Tech Stack Audit",
  support: "Priority 24/7",
  fit: "Max revenue growth",
  matched: false
}, {
  provider: "Custom",
  commission: "1%",
  features: "Full Outsourcing",
  support: "Direct Access",
  fit: "Total management",
  matched: false
}];
const brandData: Record<string, SizeRow[]> = {
  siteminder: siteminderRows,
  cloudbeds: cloudbedsRows,
  "d-edge": dedgeRows,
  hotelrevbaba: hotelrevbabaRows
};
function SizeTable({
  rows
}: {
  rows: SizeRow[];
}) {
  return <TooltipProvider delayDuration={150}>
      <Table className="border-collapse">
        <TableHeader>
          <TableRow className="border-b border-slate-200 hover:bg-transparent">
            <TableHead className="font-default text-xs uppercase tracking-[0.18em] text-slate-500 sticky left-0 bg-slate-50">
              Plan
            </TableHead>
            <TableHead className="font-default text-xs uppercase tracking-[0.18em] text-slate-500">
              Comm.
            </TableHead>
            <TableHead className="font-default text-xs uppercase tracking-[0.18em] text-slate-500">
              Features
            </TableHead>
            <TableHead className="font-default text-xs uppercase tracking-[0.18em] text-slate-500">
              Support
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, i) => <Tooltip key={i}>
              <TooltipTrigger asChild>
                <TableRow data-index={i} className="border-b border-dashed border-slate-200 odd:bg-white/50 transition-colors duration-200 hover:bg-amber-50 cursor-default">
                  <TableCell className={row.matched ? "sticky left-0 bg-slate-900 text-white font-default font-semibold text-base" : "sticky left-0 bg-slate-50 font-default text-base text-slate-900"}>
                    {row.provider}
                  </TableCell>
                  <TableCell className="font-default text-base text-slate-900">
                    {row.commission}
                  </TableCell>
                  <TableCell className="font-default text-base text-slate-900">
                    {row.features}
                  </TableCell>
                  <TableCell className="font-default text-base text-slate-900">
                    {row.support}
                  </TableCell>
                </TableRow>
              </TooltipTrigger>
              <TooltipContent side="top" className="bg-slate-900 text-white font-default text-[0.7rem] uppercase tracking-[0.12em]">
                {row.fit}
              </TooltipContent>
            </Tooltip>)}
        </TableBody>
      </Table>
    </TooltipProvider>;
}
export default function Bookingenginecomparison() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="1935" id="bookingenginecomparison" className="bg-slate-50 text-slate-900 py-24 md:py-36 px-5 md:px-10">
      <div className="mx-auto max-w-[1320px]">
        <div ref={ref} className={"relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-end transition-all duration-700 " + (isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6")}>
          <span aria-hidden="true" className="pointer-events-none absolute -top-10 left-0 select-none font-default font-bold text-slate-900/[0.06] leading-none text-[8rem] md:text-[12rem]">
            02
          </span>

          <div className="lg:col-span-8 relative z-10">
            <p className="font-default text-xs md:text-sm uppercase tracking-[0.22em] text-slate-600 mb-6">
              § 02 / REVENUE SOLUTIONS
            </p>
            <h2 className="font-default font-semibold leading-[0.98] tracking-[-0.02em] text-[clamp(2rem,4.5vw,3.75rem)] text-slate-900">
              Revenue Platforms &middot; Commission &middot; Strategy,{" "}
              <span className="relative inline-block">
                Side
                <span className="absolute -bottom-1 left-0 h-[3px] w-full bg-amber-700" />
                <span className="absolute -bottom-1 right-[-6px] h-[7px] w-[7px] rounded-lg bg-amber-700" />
              </span>{" "}
              By Side
            </h2>
            <p className="mt-6 max-w-xl font-default text-base md:text-lg leading-relaxed text-slate-700">
              Compare your current booking engine costs against our data-driven revenue management strategies. Hover any row for expert insights.
            </p>
          </div>

          <div className="lg:col-span-4 relative z-10">
            <Tabs defaultValue="siteminder" className="w-full">
              <TabsList className="flex w-full flex-wrap h-auto gap-1 bg-amber-700 p-1 rounded-lg">
                {brands.map((brand, i) => <TabsTrigger key={brand.id} data-index={i} value={brand.id} className="flex-1 font-default text-xs uppercase tracking-[0.1em] data-[state=active]:bg-slate-900 data-[state=active]:text-white rounded-lg">
                    {brand.label}
                  </TabsTrigger>)}
              </TabsList>

              {brands.map((brand, i) => <TabsContent key={brand.id} data-index={i} value={brand.id} className="mt-10 animate-in fade-in-0 duration-300">
                  <div className="block md:hidden">
                    <div className="rounded-lg border border-dashed border-slate-200 p-1">
                      <ScrollArea className="w-full whitespace-nowrap">
                        <div className="min-w-[480px] p-3">
                          <SizeTable rows={brandData[brand.id]} />
                        </div>
                        <ScrollBar orientation="horizontal" />
                      </ScrollArea>
                    </div>
                  </div>
                </TabsContent>)}
            </Tabs>
          </div>
        </div>

        <div className="my-10 hidden md:block border-t border-dashed border-slate-200" />

        <div className="hidden md:block">
          <Tabs defaultValue="siteminder" className="w-full">
            <TabsList className="mb-8 inline-flex h-auto gap-1 bg-amber-700 p-1 rounded-lg">
              {brands.map((brand, i) => <TabsTrigger key={brand.id} data-index={i} value={brand.id} className="font-default text-xs uppercase tracking-[0.12em] px-5 data-[state=active]:bg-slate-900 data-[state=active]:text-white rounded-lg">
                  {brand.label}
                </TabsTrigger>)}
            </TabsList>

            {brands.map((brand, i) => <TabsContent key={brand.id} data-index={i} value={brand.id} className="animate-in fade-in-0 duration-300">
                <div className="rounded-lg border border-dashed border-slate-200 p-4 md:p-6 bg-white/20">
                  <SizeTable rows={brandData[brand.id]} />
                </div>
                <p className="mt-4 font-default text-[0.7rem] uppercase tracking-[0.12em] text-slate-500">
                  Dark row indicates the recommended tier for optimal revenue performance
                </p>
              </TabsContent>)}
          </Tabs>
        </div>
      </div>
    </section>;
}